import React, { ReactNode, useContext, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@apollo/client';
import { ViewportList } from 'react-viewport-list';
import Tippy from '@tippyjs/react';
import { SVGIcon } from '@superops/super-icons';
import { Avatar, Link, TextStyle } from '@superops/ui-design-system';
import { HomeContext } from '@nile/mangifera/src/Contexts';
import {
  DateTimeFormatter,
  ENTITY_MAP,
  convertToMSPTimeZone,
  getEntityAttributes,
  getTranslatedText,
  withErrorBoundary
} from '@nile/components';
import {
  Audit,
  AuditData,
  AuditEntity,
  AuditOperation,
  AuditParentEntity
} from '@nile/types';
import { GET_ENTITY_PROPERTIES } from '@nile/graphql';
import { getTimeAgo } from '@nile/components/src/Components/Notifications/time';
import AttributesHandler from './AttributeHandler';
import { operationMap, ActorMap } from './constants';
import ChatAuditLog from './ChatAuditLog';
import AIAuditLog from './AIAuditLog';

function getEntity(
  entity: AuditEntity,
  parentEntity: AuditParentEntity,
  data: AuditData
): string {
  switch (entity) {
    case 'VendorConversation':
      return 'sideConversation';
    case 'TicketConversation':
      const conversationType = data[0]?.attributes[0]?.value?.new;
      switch (conversationType) {
        case 'DESCRIPTION':
          return 'conversation';
        default:
          return `${parentEntity.toLowerCase()}.conversation`;
      }
    default:
      return entity.toLowerCase();
  }
}

function getMessageKeyValues(
  entity: AuditEntity,
  parentEntity: AuditParentEntity,
  data: AuditData[],
  operation: AuditOperation
): string {
  let KEY = `operation.performed.by`;
  if (entity === 'ConversationThread') {
    if (operation === 'CREATE') {
      if (data?.[0]?.attributes?.find((el) => el?.value?.new === 'PRIVATE')) {
        // Check private scope
        return `private.group.conversationThread.${KEY}`;
      }
      return 'msp.chat.updates.initiatedBy';
    }
    if (
      data?.[0]?.attributes?.find(
        (el) => el?.value?.new === 'SESSION_CONNECTED'
      )
    ) {
      return 'msp.chat.updates.connectedTo';
    }

    if (
      data?.[0]?.attributes?.find(
        (el) => el?.value?.new === 'REQUESTER_NOT_ACCEPTED'
      )
    ) {
      return 'msp.chat.updates.chat.ignore';
    }

    if (operation === 'CLOSED') {
      return 'msp.chat.updates.closedBy';
    }
    return 'msp.chat.updates.updatedBy';
  }
  if (entity === 'TicketConversation' || entity === 'VendorConversation') {
    const conversationType = data[0]?.attributes[0]?.value?.new;
    switch (conversationType) {
      case 'REQ_REPLY':
      case 'VENDOR_EMAIL':
        KEY = `reply.received.from`;
        break;
      case 'REQ_NOTIFICATION':
      case 'TECH_NOTIFICATION':
        KEY = `notification.sent.by`;
        break;
      case 'TECH_EMAIL':
      case 'TECH_REPLY':
        KEY = `reply.sent.by`;
        break;
      case 'TECH_INITIATED':
      default:
        KEY = `operation.performed.by`;
        break;
    }
  }
  if (entity === 'Ticket') {
    switch (operation) {
      case 'SPAM':
        KEY = 'marked.spam.by';
        break;
      default:
        break;
    }
  }
  return `audit.log.${getEntity(entity, parentEntity, data)}.${KEY}`;
}

function getKey(audit: Audit): string {
  switch (audit?.entity) {
    case 'TicketConversation':
    case 'VendorConversation':
      return operationMap[
        audit?.data[0]?.attributes[0]?.value?.new || 'DESCRIPTION'
      ];
    default:
      return operationMap[audit?.operation] || 'common.updated';
  }
}

function AuditLog({ audit }: { audit: Audit }): ReactNode {
  const intl = useIntl();
  const {
    parentEntity,
    entity,
    user,
    operation,
    time,
    data,
    actorLabel,
    actorId,
    actorName
  } = audit;
  const homeContext = useContext(HomeContext);

  const { data: entityFields, loading: entitiesLoading } = useQuery(
    GET_ENTITY_PROPERTIES
  );

  const AttributesMap = {};

  const ntt = entity === 'ConversationThread' ? entity : parentEntity || entity;

  getEntityAttributes(entityFields, ENTITY_MAP[ntt.toLowerCase()]).forEach(
    (attr) => {
      if (attr.name && attr.label) {
        AttributesMap[attr.name] = {
          label: attr.label,
          type: attr.dataType,
          entityType: attr?.entity
        };
      }
    }
  );

  let actorDetails: ReactNode | null = null;
  let Icon: ReactNode = <SVGIcon name="Edit" />;
  const listRef = useRef();

  switch (actorName) {
    case 'USER':
      actorDetails = (
        <div className="fx actr g5">
          <Avatar
            name={user?.name}
            initials={user?.name[0]}
            size="extraSmall"
          />
          <TextStyle variation="strong" weight="Regular">
            {user?.name}
          </TextStyle>
        </div>
      );
      break;
    case 'SLA':
    case 'TIME_TRIGGER':
    case 'EVENT_TRIGGER':
    case 'PRIORITY_MATRIX':
      actorDetails = (
        <div className="fx actr g5">
          <SVGIcon
            name={ActorMap[actorName]?.icon || 'SlaMetric'}
            color="#FF0055"
          />
          <TextStyle variation="strong" weight="Regular">
            {actorLabel}
            <span className="capitalize">
              {`(${getTranslatedText(
                intl,
                ActorMap[actorName]?.key || 'entity.sla'
              )})`}
            </span>
          </TextStyle>
        </div>
      );
      break;
    case 'UNKNOWN_USER':
      actorDetails = (
        <div className="fx actr g5">
          <Avatar
            name={
              entity === 'ConversationThread'
                ? actorLabel
                : actorName || actorLabel
            }
            initials={
              entity === 'ConversationThread'
                ? actorLabel[0]
                : actorName[0] || actorLabel[0]
            }
            size="extraSmall"
          />
          <TextStyle variation="strong" weight="Regular">
            {actorLabel}
          </TextStyle>
        </div>
      );
      break;
    default:
      actorDetails = (
        <div className="fx actr">
          <TextStyle variation="strong" weight="Regular">
            <FormattedMessage id="common.user.system" />
          </TextStyle>
        </div>
      );
      break;
  }

  switch (entity) {
    case 'Ticket':
      switch (operation) {
        case 'MERGE':
          Icon = <SVGIcon size="sm" name="Merge" color="#34AE6C" />;
          break;
        case 'UNMERGE':
          Icon = <SVGIcon size="sm" name="Merge" color="#34AE6C" />;
          break;
        case 'SPLIT':
          Icon = <SVGIcon size="sm" name="Split" color="#3DADB4" />;
          break;
        case 'TRASH':
          Icon = <SVGIcon size="sm" name="Delete" color="#D13838" />;
          break;
        case 'RESTORE':
          Icon = (
            <SVGIcon size="sm" name="RotateRefreshLoading" color="#363DDA" />
          );
          break;
        case 'RESOLVED':
          Icon = <SVGIcon size="sm" name="Resolution" color="#48AC3D" />;
          break;
        case 'CLOSED':
          Icon = <SVGIcon size="sm" name="Power" color="#D16F38" />;
          break;
        case 'SPAM':
          Icon = <SVGIcon size="sm" name="Spam" color="#283F91" />;
          break;
        case 'UPDATE':
          Icon = <SVGIcon size="sm" name="Edit" color="#3FA2C1" />;
          break;
        case 'ASSOCIATE':
          Icon = <SVGIcon size="sm" name="Frequency" color="#3FA2C1" />;
          break;
        default:
          Icon = <SVGIcon size="sm" name="TicketIdentifier" color="#091EF5" />;
          break;
      }
      break;
    case 'TicketConversation':
      switch (data[0]?.attributes[0]?.value?.new) {
        case 'REQ_NOTIFICATION':
        case 'TECH_NOTIFICATION':
          Icon = <SVGIcon size="sm" name="NotificationsBell" color="#5043A8" />;
          break;
        case 'TECH_REPLY':
          Icon = <SVGIcon size="sm" name="OutgoingMail" color="#41B637" />;
          break;
        case 'REQ_REPLY':
          Icon = <SVGIcon size="sm" name="IncomingMail" color="#F9446E" />;
          break;
        default:
          Icon = <SVGIcon size="sm" name="ReplyBubble" color="#40AEFE" />;
          break;
      }
      break;
    case 'Note':
      switch (operation) {
        case 'DELETE':
          Icon = <SVGIcon size="sm" name="Delete" color="#D13838" />;
          break;
        case 'UPDATE':
          Icon = <SVGIcon size="sm" name="Edit" color="#3FA2C1" />;
          break;
        default:
          Icon = <SVGIcon size="sm" name="Notes" color="#C7AE3D" />;
          break;
      }
      break;
    case 'VendorConversation':
      Icon = <SVGIcon size="sm" name="Helpcenter" color="#29BDDD" />;
      break;
    case 'ConversationThread':
      if (
        data?.[0]?.attributes?.find((el: any) => el?.value?.new === 'PRIVATE')
      ) {
        // Check if private scope
        Icon = <SVGIcon size="sm" name="PrivateArticlePlain" color="#00D247" />;
      } else {
        Icon = (
          <SVGIcon size="sm" name="ConversationThreadLog" color="#34AE6C" />
        );
      }
      break;
    default:
      Icon = <SVGIcon size="sm" name="TicketIdentifier" color="#F9446E" />;
      break;
  }

  if (actorName === 'AI_AGENT') {
    return <AIAuditLog audit={audit} entityAttributesMap={AttributesMap} />;
  }

  if (entity === 'ConversationThread') {
    return (
      <ChatAuditLog
        audit={audit}
        intl={intl}
        icon={Icon}
        actorDetails={actorDetails}
        getMessageKeyValues={getMessageKeyValues}
        getKey={getKey}
        AttributesMap={AttributesMap}
      />
    );
  }

  return (
    <div
      className="fx col g10 p10 rounded-2xl"
      style={{ backgroundColor: 'var(--sops-white-to-grey-bg)' }}
    >
      <div className="br-5">
        <div className="fx actr g5 wrap">
          <span className="fx actr mr-5">{Icon}</span>
          <FormattedMessage
            id={getMessageKeyValues(entity, parentEntity, data, operation)}
            values={{
              AuditlogOperation: (
                <TextStyle variation="strong" weight="Bold" uppercase>
                  {getTranslatedText(intl, getKey(audit))}
                </TextStyle>
              ),
              strong: (chunks) => <strong>{chunks}</strong>
            }}
          />
          {actorDetails}
          {entity === 'TicketConversation' &&
            (data[0]?.attributes[0]?.value?.new === 'REQ_NOTIFICATION' ||
              data[0]?.attributes[0]?.value?.new === 'TECH_NOTIFICATION') && (
              <span className="fx actr g5">
                <FormattedMessage id="admin.eventTrigger.to" />{' '}
                <FormattedMessage
                  id={
                    data[0]?.attributes[0]?.value?.new === 'REQ_NOTIFICATION'
                      ? 'entity.requester'
                      : 'app.common.technician'
                  }
                />
              </span>
            )}
        </div>
        <span className="ml-26 d-in">
          <TextStyle variation="subdued">
            <Tippy
              content={
                <div className="p0510">
                  <DateTimeFormatter type="dateTime" date={time} />
                </div>
              }
              duration={250}
              delay={250}
              interactive
              placement="bottom-start"
              arrow={false}
              allowHTML
              theme="dark"
              appendTo={document.body}
            >
              <div>
                {getTimeAgo(time.toString(), convertToMSPTimeZone(new Date()))}
              </div>
            </Tippy>
          </TextStyle>
        </span>
        {entity !== 'TicketConversation' &&
          entity !== 'VendorConversation' &&
          data?.length && (
            <div className="fx col g10 lightpurple-to-black-bg mt5 px-25 br-5">
              <ViewportList
                viewportRef={listRef}
                items={data}
                itemMinSize={100}
                initialPrerender={4}
              >
                {(el: AuditData): ReactNode => {
                  if (el?.value) {
                    const splitOrMergeDetails = el?.value[el?.value?.type];
                    return (
                      <div className="fx actr wrap line-break-anywhere g5">
                        <TextStyle variation="strong">
                          <FormattedMessage
                            id={`audit.log.ticket.data.${operation.toLowerCase()}.${
                              el?.value?.type
                            }`}
                          />
                        </TextStyle>
                        :{' '}
                        {splitOrMergeDetails.length ? (
                          <div className="fx actr wrap g5">
                            {splitOrMergeDetails.map((splitOrMergeTicket) => {
                              return (
                                <div
                                  className="linkText"
                                  onClick={() => {
                                    if (splitOrMergeTicket?.id) {
                                      homeContext.openTab({
                                        module: 'TICKET',
                                        data: {
                                          ticketId: `${splitOrMergeTicket.id}`
                                        },
                                        ticketId: `${splitOrMergeTicket?.id}`
                                      });
                                    }
                                  }}
                                >
                                  <FormattedMessage id="common.ticket" /> #
                                  {
                                    splitOrMergeTicket?.attributes[0]?.value
                                      ?.new
                                  }
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div
                            className="linkText"
                            onClick={() => {
                              if (splitOrMergeDetails?.id) {
                                homeContext.openTab({
                                  module: 'TICKET',
                                  data: {
                                    ticketId: `${splitOrMergeDetails?.id}`
                                  },
                                  ticketId: `${splitOrMergeDetails?.id}`
                                });
                              }
                            }}
                          >
                            <FormattedMessage id="common.ticket" /> #
                            {splitOrMergeDetails?.attributes[0]?.value?.new}
                          </div>
                        )}
                      </div>
                    );
                  }

                  if (el?.attributes) {
                    return (
                      <AttributesHandler
                        skipName={false}
                        showInitial={false}
                        attributes={el.attributes}
                        entityAttributesMap={AttributesMap}
                        entityName={entity}
                      />
                    );
                  }
                  return null;
                }}
              </ViewportList>
            </div>
          )}
      </div>
    </div>
  );
}

export default withErrorBoundary(AuditLog);
